import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { SortableIndicator } from '~/modules/artist-roster/components/Table/components/SortableIndicator/SortableIndicator';
import { formatDateByLocale } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TrackList/utils';
import placeHolderImage from '~/modules/artist-roster/pages/track-details/images/track_placeholder.svg?url';
import type { ArtistDetails, AssetFamily } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { PreReleaseTableFeedbackCell } from './PreReleaseTableFeedbackCell';

export function usePreReleaseTableColumns(
  handlePreReleaseTableRowClick: () => void,
  artistData: ArtistDetails,
) {
  const { t } = useTranslation('roster');

  const [isSetBenchmarksEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgTrackSetBenchmarks,
  ]);

  return useMemo(() => {
    const columnHelper = createColumnHelper<AssetFamily>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preReleaseTableColumns: ColumnDef<AssetFamily, any>[] = [
      columnHelper.accessor('title', {
        id: 'title',
        enableSorting: true,
        header: () => {
          return (
            <div className="flex items-center gap-sm">
              <span>{t('preReleases.table.title')}</span>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex items-center gap-md py-sm">
            <div>
              <img
                src={row.original.image ?? placeHolderImage}
                alt={row.original.title}
                className="size-[3rem] place-self-center rounded-xs object-cover"
              />
            </div>
            <div className="font-bold">{row.original.title}</div>
          </div>
        ),
      }),
      columnHelper.accessor('initialReleaseDate', {
        id: 'initialReleaseDate',
        enableSorting: true,
        header: (header) => {
          return (
            <div className="flex gap-sm">
              <span>{t('preReleases.table.releaseDate')}</span>
              <SortableIndicator
                column={header.column}
                tooltip={t('trackList.tooltips.sortBy', {
                  field: t('preReleases.table.releaseDate').toLowerCase(),
                })}
              />
            </div>
          );
        },
        cell: ({ row }) => (
          <div>
            {row.original.initialReleaseDate ? (
              formatDateByLocale(row.original.initialReleaseDate)
            ) : (
              <span className="px-sm text-subtle">--</span>
            )}
          </div>
        ),
      }),
    ];

    preReleaseTableColumns.push(
      columnHelper.accessor('benchmark.firstWeekStreams', {
        id: 'firstWeekStreams',
        enableSorting: false,
        header: () => (
          <div className="flex items-end gap-xs text-nowrap">
            <span>{t('preReleases.table.firstWeek')}</span>
          </div>
        ),
        cell: ({ row }) => {
          const firstWeekStreams =
            row.original?.benchmark?.firstWeekStreams?.toLocaleString();

          return (
            firstWeekStreams || <span className="px-sm text-subtle">--</span>
          );
        },
      }),

      columnHelper.accessor('benchmark.secondWeekStreams', {
        id: 'secondWeekStreams',
        enableSorting: false,
        header: () => (
          <div className="flex items-end gap-xs text-nowrap">
            <span>{t('preReleases.table.secondWeek')}</span>
          </div>
        ),
        cell: ({ row }) => {
          const secondWeekStreams =
            row.original?.benchmark?.secondWeekStreams?.toLocaleString();

          return (
            secondWeekStreams || <span className="px-sm text-subtle">--</span>
          );
        },
      }),

      columnHelper.display({
        id: 'feedback',
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <PreReleaseTableFeedbackCell
            asset={row.original}
            artistData={artistData}
          />
        ),
      }),
    );

    if (isSetBenchmarksEnabled) {
      preReleaseTableColumns.push(
        columnHelper.display({
          id: 'benchmarkAction',
          enableSorting: false,
          header: () => null,
          cell: ({ row }) => {
            const isAlreadyAdjusted =
              row.original.benchmark?.adjustedPercent !== undefined &&
              row.original.benchmark.adjustedPercent !== 0;

            return (
              <Button
                variant={isAlreadyAdjusted ? 'tertiary' : 'secondary'}
                className="text-body-sm font-semibold"
                onClick={() => handlePreReleaseTableRowClick()}
              >
                {isAlreadyAdjusted
                  ? t('trackPreReleaseList.benchmarkPanel.editBenchmark')
                  : t('trackPreReleaseList.benchmarkPanel.setBenchmark')}
              </Button>
            );
          },
        }),
      );
    }

    return preReleaseTableColumns;
  }, [t, isSetBenchmarksEnabled, handlePreReleaseTableRowClick, artistData]);
}
